import { Injectable } from "@angular/core";
import { TaxReturnTypeService, TaxReturnSubTypeService, TaxReturnType, TaxReturnSubType } from "../../../swaggerServices";
import { DropdownListOption } from "../components/search-dropdown/search-dropdown.component";
import { of, Subject, BehaviorSubject, Observable, forkJoin } from "rxjs";
import { TypeSubtypeLookup, generateSubtypeChains, subtypeToDropdownListOptions, typeToDropdownListOptions } from "./tax-return-type-subtype";
import { map } from "rxjs/operators";

@Injectable()
export class TaxReturnTypeSubTypeService {

  private taxReturnTypes: any[];
  $taxReturnTypes: Subject<any> = new BehaviorSubject({});

  private taxReturnSubTypes: any[];
  private taxReturnSubTypesOptions: any[];
  $taxReturnSubTypes: Subject<any> = new BehaviorSubject({});

  constructor(
    private typeService: TaxReturnTypeService,
    private subtypeService: TaxReturnSubTypeService) { }

  // Returns DropdownListOptions[]
  getTaxReturnTypes(allowAll?: boolean) {
    if (this.taxReturnTypes) {
      this.$taxReturnTypes.next(this.taxReturnTypes);
    }
    return this.typeService.getTaxReturnTypes()
      .pipe(map((taxReturnTypes: TaxReturnType[]) => {
      const activeTaxReturnTypes = taxReturnTypes.filter(t => !t.status);
      const options: DropdownListOption[] = [];

      if (allowAll && activeTaxReturnTypes.length > 1) {
        options.push({
          id: -1,
          name: 'All',
          value: '0',
          order: -1,
        });
      }

      activeTaxReturnTypes.forEach((trt, index) => {
        options.push(typeToDropdownListOptions(trt, index));
      });

      this.taxReturnTypes = options;
      this.$taxReturnTypes.next(this.taxReturnTypes);
      return this.taxReturnTypes;
    }));
  };

  // Returns DropdownListOptions[]
  getTaxReturnSubTypesByType(taxReturnTypeId: number, excludeId: number = -1) {
    if (this.taxReturnSubTypesOptions) {
      this.$taxReturnSubTypes.next(this.taxReturnSubTypesOptions);
    }
    return this.subtypeService.getTaxReturnSubTypesBytype(taxReturnTypeId).pipe(
      map(({ subtypes }) => {
        const activeTaxReturnSubTypes = subtypes.filter(s => !s.status);
        const options: DropdownListOption[] = [];

        activeTaxReturnSubTypes.forEach((subtype, index) => {
          if (subtype.id != excludeId) {
            options.push(subtypeToDropdownListOptions(subtype, index));
          }
        });

        this.taxReturnSubTypes = activeTaxReturnSubTypes;
        this.taxReturnSubTypesOptions = options;
        this.$taxReturnSubTypes.next(this.taxReturnSubTypesOptions);
        return this.taxReturnSubTypesOptions;
      })
    );
  }

  // Returns DropdownListOptions[]
  getTaxReturnSubTypes() {
    if (this.taxReturnSubTypesOptions) {
      this.$taxReturnSubTypes.next(this.taxReturnSubTypesOptions);
    }
    return this.subtypeService.getTaxReturnSubTypes().pipe(
      map(({ subtypes }) => {
        const activeTaxReturnSubTypes = subtypes.filter(s => !s.status);
        const options: DropdownListOption[] = [];
        activeTaxReturnSubTypes.forEach((subtype, index) => {
          options.push(subtypeToDropdownListOptions(subtype, index));
        });

        this.taxReturnSubTypes = activeTaxReturnSubTypes;
        this.taxReturnSubTypesOptions = options;
        this.$taxReturnSubTypes.next(this.taxReturnSubTypesOptions);
        return this.taxReturnSubTypesOptions;
      })
    );
  }

  doesSettingPriorSubTypeGeneratesLoops(subType, priorSubTypeId) {
    //go back up the chain until we reach an end or ourself
    const chains = {};
    this.taxReturnSubTypes.filter(t => t.id).forEach(t => {
      chains[t.id] = t.priorSubTypeId;
    });
    chains[subType.id] = priorSubTypeId;

    let hasLoop = false;
    let keepChecking = true;
    let nodeId = subType.id;
    while (keepChecking && !hasLoop) {
      let priorId = chains[nodeId];

      if (priorId) {
        nodeId = priorId;
        hasLoop = (nodeId === subType.id);
        keepChecking = !!nodeId;
      } else {
        keepChecking = false;
        hasLoop = false;
      }
    }
    return hasLoop;
  }

  getAllTypesAndSubtypes(isGlobal = false): Observable<TypeSubtypeLookup> {

    const types: Map<string, TaxReturnType> = new Map();
    const subtypes: Map<string, TaxReturnSubType> = new Map();
    const chains: Map<string, TaxReturnSubType[]> = new Map();

    if (isGlobal) {
      return of<TypeSubtypeLookup>(new TypeSubtypeLookup(types, subtypes, chains));
    }

    const typesObs = this.typeService.getTaxReturnTypes();
    const subtypesObs = this.subtypeService.getTaxReturnSubTypes();
    return forkJoin([typesObs, subtypesObs])
      .pipe(
        map(([trTypes, { subtypes:trSubtypes }]) => {

          trTypes.forEach(t => { types.set(t.id.toString(), t); });
          trSubtypes.forEach(t => { subtypes.set(t.id.toString(), t); });

          const subtypeChains = generateSubtypeChains(trSubtypes);
          subtypeChains.forEach(c => { chains.set(c.firstSubtype.id.toString(), c.chain); });
          return new TypeSubtypeLookup(types, subtypes, chains);
        }
      )
    );
  }
}
